import { defineStore } from 'pinia'
import { ref } from 'vue'

export type AppLocale = 'en' | 'es' | 'pt'

/**
 * Portuguese-speaking IANA zones. The OS timezone, not GPS.
 */
const PT_ZONES = new Set([
  'America/Noronha',
  'America/Belem',
  'America/Fortaleza',
  'America/Recife',
  'America/Araguaina',
  'America/Maceio',
  'America/Bahia',
  'America/Sao_Paulo',
  'America/Campo_Grande',
  'America/Cuiaba',
  'America/Santarem',
  'America/Porto_Velho',
  'America/Boa_Vista',
  'America/Manaus',
  'America/Eirunepe',
  'America/Rio_Branco',
  'Europe/Lisbon',
  'Atlantic/Madeira',
  'Atlantic/Azores',
  'Atlantic/Cape_Verde',
  'Africa/Luanda',
  'Africa/Maputo',
  'Africa/Bissau',
  'Africa/Sao_Tome',
  'Asia/Dili',
])

/**
 * Spanish-speaking IANA zones, outside the Argentina prefix.
 */
const ES_ZONES = new Set([
  'America/Bahia_Banderas',
  'America/Cancun',
  'America/Chihuahua',
  'America/Ciudad_Juarez',
  'America/Hermosillo',
  'America/Matamoros',
  'America/Mazatlan',
  'America/Merida',
  'America/Mexico_City',
  'America/Monterrey',
  'America/Ojinaga',
  'America/Tijuana',
  'Europe/Madrid',
  'Africa/Ceuta',
  'Atlantic/Canary',
  'America/Bogota',
  'America/Lima',
  'America/Santiago',
  'America/Punta_Arenas',
  'America/Coyhaique',
  'Pacific/Easter',
  'America/Caracas',
  'America/Guayaquil',
  'Pacific/Galapagos',
  'America/La_Paz',
  'America/Asuncion',
  'America/Montevideo',
  'America/Guatemala',
  'America/Tegucigalpa',
  'America/El_Salvador',
  'America/Managua',
  'America/Costa_Rica',
  'America/Panama',
  'America/Havana',
  'America/Santo_Domingo',
  'America/Puerto_Rico',
  'Africa/Malabo',
])

/**
 * BCP 47 tag for dates and `document.documentElement.lang` stays the short code.
 */
export function intlLocale(locale: AppLocale): string {
  if (locale === 'pt') return 'pt-BR'
  if (locale === 'es') return 'es-419'
  return 'en-US'
}

function zoneLocale(zone: string): AppLocale | null {
  if (PT_ZONES.has(zone)) return 'pt'
  if (ES_ZONES.has(zone) || zone.startsWith('America/Argentina/')) return 'es'
  return null
}

/**
 * First visit only. A saved locale in Pinia wins over this.
 * Browser language wins when it is Portuguese or Spanish.
 * Otherwise the OS timezone is a location hint, with no permission prompt.
 */
function detectLocale(): AppLocale {
  try {
    const primary = (typeof navigator !== 'undefined' ? navigator.language : '')
      .toLowerCase()
      .split('-')[0]
    if (primary === 'pt' || primary === 'es') return primary
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return zoneLocale(zone) ?? 'en'
  } catch {
    return 'en'
  }
}

export const useLocaleStore = defineStore(
  'locale',
  () => {
    const locale = ref<AppLocale>(detectLocale())

    function setLocale(next: AppLocale): void {
      locale.value = next
    }

    return { locale, setLocale }
  },
  {
    persist: {
      paths: ['locale'],
    },
  },
)
